import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Product } from '~shared/models';
import { CountryCode, FOOD_CATEGORY, NUMBER_0, NUMBER_1, NUMBER_1000, NUMBER_3000 } from '~shared/constants';
import { environment } from '~env/environment';
import { ProductCardImageUtil } from '../../utils/product-card-image.util';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services/price-type.service';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { ProductUtil } from '~shared/utils/product.util';
import { PDPNavigatorService } from '~shared/modules/pdp-navigator/pdp-navigator.service';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { SelectItemApplicationEventModel } from '~shared/modules/application-event/models';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '~app/modules/generic/constants';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { Params } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { getQueryParams } from '~core/store';
import { shareReplay, take, takeUntil } from 'rxjs/operators';
import { CatalogState } from '~app/modules/catalog/store';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { UrlUtil } from '~shared/utils/url.util';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { EnergyLabelScaleService } from '~shared/modules/product-card/services/energy-label-scale.service';
import { GetCategoryTreeArray } from '~app/modules/generic/helpers';
import { GaTrackingStringConstantEnum } from '~shared/modules/ga-tracking/constants';
import { GaTrackingHelpersService } from '~shared/modules/ga-tracking/utils/ga-tracking-helpers.service';
import { getBlackWeekPaylodProperties } from '~shared/modules/ga-tracking/utils/ga-tracking-black-week.util';
import * as fromGlobalCart from '~shared/modules/shopping-cart-global/store';
import { CartState } from '~shared/modules/shopping-cart-store';
import { Category } from '~app/modules/generic/models';
import { CartNotificationsService } from '~shared/modules/shopping-cart-global/services/cart-notifications.service';
import { AddToCartGa4ApplicationEvent } from '~shared/modules/application-event/models/add-to-cart-application-ga4-event.model';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { AddToCardConfig } from '~shared/modules/product-card/models/add-to-card-config.model';

@Component({
  selector: 'app-search-product-card-variants',
  templateUrl: './product-card-variants.component.html',
  styleUrls: ['./product-card-variants.component.scss'],
})
export class ProductCardVariantsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() product: Product;
  @Input() isDesktop: boolean;
  @Input() isProductList = true;
  @Input() isCustomPLPPage = false;
  @Input() isBlackDealsEnabled: boolean;
  @Input() showOnlyImageOnSimilarProductsCarousel = false;
  @Input() isSimilarProductRecommendation: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() productIndex: number;
  @Input() shouldSaveSpaceForVariants: {
    hasColors: boolean;
    hasSizes: boolean;
  };
  @Input() shouldSaveSpaceForAddToCart: boolean;
  @Input() saveTotalUnitsOrBasePriceSpace: boolean;
  @Input() carouselProductCard: boolean;
  @Input() addToCartConfig: AddToCardConfig;
  @Input() fromCarousel?: boolean
  @Input() title?: string
  @Input() trackingName?: string

  @Output() productClick = new EventEmitter<{ product: Product; priceType: PriceTypes }>();

  priceType$: Observable<PriceTypes>;
  queryParams$: Observable<Params> = this.loadQueryParams();
  queryParams: Params;
  public page: number = 1;
  public limit: number = DEFAULT_LIMIT;
  currentUrl = this.navigationHelper.getCurrentUrl();
  pageType = UrlUtil.getPageTypeFromCurrentUrl(this.currentUrl);

  defaultProductImage = `${environment.cdnBaseUrl()}/images/product-catalog-placeholder.png`;
  minAmount: number = NUMBER_1;

  isBlackDealProduct: boolean;
  isLimitedStock: boolean;
  shouldApplyZeroVAT: boolean;
  country: CountryCode;
  baseProductPDPLink: string;
  showEnergyScale: boolean;
  addToCartLoading: boolean = false;
  addingToCart: boolean = false;
  cartSuccessNotification: Subject<any> = this.cartNotificationsService.cartSuccessNotifier;
  isProductAddedToCart$ = new BehaviorSubject<Product>(null);
  qtyTooltip: boolean;
  quantity: number = 1;
  private destroy$ = new Subject<void>();

  constructor(
    private priceTypeService: PriceTypeService,
    private PDPNavigator: PDPNavigatorService,
    private store$: Store<CatalogState>,
    private applicationEventService: ApplicationEventService,
    private navigationHelper: NavigationHelper,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private energyLabelScaleService: EnergyLabelScaleService,
    private priceTypeSvc: PriceTypeService,
    private cartStore$: Store<CartState>,
    private changeDetectorRef: ChangeDetectorRef,
    private cartNotificationsService: CartNotificationsService,
    private productsListUrlService: ProductsListUrlService
  ) {}

  get isProductAddToCartAllowed() {
    const allowdCategories = new Set([FOOD_CATEGORY]);
    return this.product.categories?.some((category: Category) => allowdCategories.has(category?.id));
  }

  getMinAmount(): number {
    if (this.addToCartConfig?.shouldHideMOQ) {
      return NUMBER_1;
    }
    const bestOfferMinQuantityAttribute = this.product?.attributes?.mpq;
    const bestOfferStock: number = this.product?.bestOffer?.quantity;
    let minAmount: number = NUMBER_1;

    const isMOQEnabled: boolean = bestOfferMinQuantityAttribute > NUMBER_0;

    if (isMOQEnabled && bestOfferStock > bestOfferMinQuantityAttribute) {
      minAmount = bestOfferMinQuantityAttribute;
    } else if (isMOQEnabled && bestOfferStock < bestOfferMinQuantityAttribute) {
      minAmount = bestOfferStock;
    }

    return minAmount;
  }

  ngOnInit(): void {
    this.showEnergyScale = this.energyLabelScaleService.shouldShowScaleEnergyLabel(this.product?.categories);
    this.priceType$ = this.priceTypeService.priceType$;
    this.priceType$.subscribe((priceType: PriceTypes) => {
      const isUserB2B = priceType === PriceTypes.b2b;
      const isBestOfferCrossBorder = this.product.bestOffer?.origin !== this.product.bestOffer?.destination;
      this.shouldApplyZeroVAT = isBestOfferCrossBorder && isUserB2B;
    });
    this.country = getCountryDomainCode(this.webAppBuyerRequest);
    this.baseProductPDPLink = this.PDPNavigator.getPdpLink(this.product?.id);
    this.cartSuccessNotification.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isProductAddedToCart$.pipe(take(1)).subscribe((product) => {
        if (product?.id === this.product?.id) {
          this.addingToCart = true;
          setTimeout(() => {
            this.addingToCart = false;
            this.changeDetectorRef.markForCheck();
          }, NUMBER_3000);
        }
        this.changeDetectorRef.detectChanges();
      });
    });

    const minAmount = this.getMinAmount();
    this.minAmount = minAmount;
    this.quantity = minAmount;
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // We don't need to do anything unless the product changed, and it's available
    if (!changes['product'] || !changes['product'].currentValue) {
      return;
    }
    if (changes['queryParams']) {
      this.updatePage();
    }
    this.queryParams$.pipe(takeUntil(this.destroy$)).subscribe((queryParam) => (this.queryParams = queryParam));

    this.isBlackDealProduct = ProductUtil.isBlackDealProduct(this.product.tags);
    this.isLimitedStock = !!this.product.bestOffer && ProductUtil.isLimitedStock(this.product.bestOffer.quantity);

    if (changes['addToCartConfig']) {
      const minAmount = this.getMinAmount();
      this.minAmount = minAmount;
      this.quantity = minAmount;
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onProductClick(product: Product, priceType: PriceTypes, e: Event): void {
    e.preventDefault();
    this.productClick.emit({
      product,
      priceType,
    });
    this.PDPNavigator.navigateToPDP(e, product.id, this.isSimilarProductRecommendation ? '?fromSimilarProducts=1' : '', this.fromCarousel)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  onAddToCart(event, product: Product) {
    const searchTerm: string = this.productsListUrlService.getSearchTerm();
    event.preventDefault();
    event.stopPropagation();

    this.addToCartLoading = true;
    const accountType: PriceTypes = this.priceTypeSvc.getPriceTypeSync();
    const categoryTreeArray = GetCategoryTreeArray(this.product?.categories);
    let dimension42 = '0';

    const addToCartData = {
      offer: product.bestOffer.id,
      productId: product.id,
      quantity: this.getMinAmount(),
      accountType,
      trackingData: {
        categoryTree: categoryTreeArray,
        leafCategoryName: categoryTreeArray[categoryTreeArray.length - NUMBER_1]?.name,
        volumePricingShown:
          product.bestOffer.volumePrice?.length > 0
            ? GaTrackingStringConstantEnum.Yes
            : GaTrackingStringConstantEnum.No,
        otherOffersShown: GaTrackingStringConstantEnum.No,
        onSale: product?.hasPromotion,
        sellerName: product.bestOffer?.sellerName,
        dimension31: GaTrackingHelpersService.getDimension31FromProduct(product),
        recommendation: false,
        dimension42,
        ...getBlackWeekPaylodProperties(this.isBlackDealsEnabled, product.bestOffer),
        dimension54: product.bestOffer.id,
      },
    };
    this.cartStore$.dispatch(new fromGlobalCart.AddToCart(addToCartData as any));
    this.applicationEventService.publish(
      new AddToCartGa4ApplicationEvent(
        this.product as any,
        product?.bestOffer?.id,
        NUMBER_1,
        this.priceTypeService.getPriceTypeSync(),
        [...this.product.categories].reverse(),
        false,
        '',
        GaTrackingStringConstantEnum.No,
        '',
        this.isBlackDealsEnabled,
        searchTerm,
        this.pageType
      )
    );
    this.isProductAddedToCart$.next(product);

    if (this.product?.attributes?.mpq <= this.product?.bestOffer?.quantity) {
      this.quantity = this.product?.attributes?.mpq;
    } else if (this.product?.attributes?.mpq > this.product?.bestOffer?.quantity) {
      this.quantity = this.product?.bestOffer?.quantity;
    } else {
      this.quantity = NUMBER_1;
    }

    setTimeout(() => {
      this.addToCartLoading = false;
      this.changeDetectorRef.detectChanges();
    }, NUMBER_1000);
  }

  getSourceSet(imageUrl: string): string {
    return ProductCardImageUtil.getSourceSet(imageUrl);
  }

  onVariantSelected(colorVariantId: string, product: Product, priceType: PriceTypes) {
    const pageOffset: number = (this.page - 1) * this.limit + this.productIndex;
    const index: number = this.isProductList ? pageOffset : this.productIndex;

    this.applicationEventService.publish(
      new SelectItemApplicationEventModel(
        `${this.pageType} carousel|${this.title}|${this.trackingName || "1"}`, 
        product, 
        priceType, 
        index,
        null,
        null, 
        colorVariantId
      )
    );
  }

  onQuantityPrettified() {
    this.qtyTooltip = true;
  }

  hideQtyNotEnoughAlert(): void {
    this.qtyTooltip = false;
  }

  onQuantityChanged(product: Product, quantity: number) {
    if (product?.bestOffer?.quantity > quantity) {
      this.qtyTooltip = false;
    }
    this.quantity = quantity;
  }

  onDisabledIncrease() {
    this.onQuantityPrettified();
  }

  private loadQueryParams(): Observable<Params> {
    return this.store$.pipe(select(getQueryParams), shareReplay(1));
  }

  private updatePage(): void {
    const offset: number = +this.queryParams.offset || DEFAULT_OFFSET;
    const limits: number = +this.queryParams.limit || this.limit;

    this.page = UrlUtils.getCurrentPage(offset, limits);
  }
}
